import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import map from 'lodash/map';
import filter from 'lodash/filter';

import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import AutoCompleteField from 'src/components/AutoCompleteField';
import ConfirmModal from 'src/components/ConfirmModal';

import {
  useCreateCandidateCertificate,
  useGetCertificates,
} from 'src/pages/CandidatesDetailsPage/api';

import messages from './messages';

interface CertificateListType {
  code: string;
  id: string;
  title: string;
}

interface CertificatesFormModalProps {
  uuid: string;
  certificates?: CertificateListType[];
}

export default function CertificatesFormModal({
  uuid,
  certificates,
}: CertificatesFormModalProps) {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);
  const [certificateCode, setCertificateCode] = useState('');

  const { data: certificateOptions } = useGetCertificates({
    enabled: open,
  });

  const {
    mutate: createCandidateCertificate,
    isPending: isCreatingCandidateCertificate,
  } = useCreateCandidateCertificate();

  const filteredCertificateOptions = useMemo(() => {
    const certificateCodes = map(certificates, ({ code }) => code);

    return filter(
      certificateOptions,
      ({ value }) => !certificateCodes.includes(value),
    );
  }, [certificates, certificateOptions]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleChangeCertificate = useCallback(
    (
      newValue:
        | { label: string; value: string }
        | { label: string; value: string }[]
        | null,
    ) => {
      if (!newValue && !Array.isArray(newValue)) return;
      if ('value' in newValue) {
        setCertificateCode(newValue.value);
      }
    },
    [],
  );

  const handleAddCertificate = useCallback(async () => {
    return new Promise<boolean>((resolve) => {
      createCandidateCertificate(
        {
          uuid,
          certificateCode,
        },
        {
          onSuccess: () => {
            handleClose();
            resolve(true);
          },
          onError: () => resolve(false),
        },
      );
    });
  }, [uuid, certificateCode]);

  return (
    <ConfirmModal
      open={open}
      onClose={handleClose}
      onConfirm={handleAddCertificate}
      title={formatMessage(messages.title)}
      triggerButton={() => (
        <IconButton onClick={handleOpen}>
          <AddCircleOutlineOutlinedIcon />
        </IconButton>
      )}
      disabled={!certificateCode}
      loading={isCreatingCandidateCertificate}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="inherit" mb={2}>
            {formatMessage(messages.message)}
          </Typography>
          <AutoCompleteField
            size="medium"
            label={formatMessage(messages.certificates)}
            options={filteredCertificateOptions ?? []}
            value={certificateCode}
            onChange={handleChangeCertificate}
          />
        </Grid>
      </Grid>
    </ConfirmModal>
  );
}
